import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadDispensedDataCigna, loadRxDetails, getLockedItems, lockRXDetails } from '../../Services/loadOrder'; // ⬅️ add getLockedItems
import MenuBar from './MenuBar';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import "../../assets/css/home.css";
import toast ,{ Toaster} from 'react-hot-toast'; 
import { exportToExcel } from '../../utils/exportToExcel';
import { FaFileExcel } from 'react-icons/fa';
import Button from '@mui/material/Button';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';  

const DispensedOrderCignaGrid = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [names, setNames] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  // Optional visibility/debug
  const [lockedTaskIds, setLockedTaskIds] = useState([]);

  const user = JSON.parse(localStorage.getItem('user'));

  const columns = [
    { field: 'memberId', headerName: 'Member ID', flex:1, headerClassName: 'pink-header' },
    { field: 'lastName', headerName: 'Last Name', flex:1, headerClassName: 'pink-header' },
    { field: 'firstName', headerName: 'First Name', flex:1, headerClassName: 'pink-header' },
    { field: 'authNo', headerName: 'Auth No', flex:1, headerClassName: 'pink-header' },
    { field: 'winAuthNo', headerName: 'Win Auth No', flex:1, headerClassName: 'pink-header' },
    { field: 'dispensedDate', headerName: 'Dispensed Date', flex:1, headerClassName: 'pink-header' },
    { field: 'dispenseBy', headerName: 'Dispense By', flex:1, headerClassName: 'pink-header',
      renderCell: (params) => (
        <div style={{ whiteSpace:'normal', wordBreak:'break-word', overflowWrap:'break-word', lineHeight:1.3, padding:'10px 2px', width:'100%' }}>
          {params.value}
        </div>
      ),
    },
    { field: 'priority', headerName: 'Priority', flex:1, headerClassName: 'pink-header' },   
    { field: 'contract', headerName: 'Contract', flex:1, headerClassName: 'pink-header',
      renderCell: (params) => (
        <div style={{ whiteSpace:'normal', wordBreak:'break-word', overflowWrap:'break-word', lineHeight:1.3, padding:'10px 2px', width:'100%' }}>
          {params.value}
        </div>
      ),
    },
    { field: 'providerName', headerName: 'Provider Name', width: 167, headerClassName: 'pink-header',
      renderCell: (params) => (
        <div style={{ whiteSpace:'normal', wordBreak:'break-word', overflowWrap:'break-word', lineHeight:1.3, padding:'10px 2px', width:'100%' }}>
          {params.value}
        </div>
      ),
    },
  ];

  // Build search dropdown from columns
  useEffect(() => {
    const searchFields = columns.map(col => ({
      label: col.headerName,
      field: col.searchField || col.field
    }));
    setNames(searchFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔐 Lock-aware binder (normalize TaskId/TaskID/Taskid + string keys)
  const bindRows = async (data, pharmacyId, userId) => {
    try {
      const lockedResp = pharmacyId ? await getLockedItems(pharmacyId) : null;
      // Build a map keyed by string TaskId
      const lockedMap = new Map();
      if (lockedResp?.success) {
        const taskList = lockedResp?.data?.TaskDetail ?? lockedResp?.data?.TaskDetails ?? lockedResp?.data ?? [];
        if (Array.isArray(taskList)) {
          taskList.forEach(it => {
            const tid = String(it.TaskId ?? it.TaskID ?? it.taskId ?? it.taskID ?? '').trim();
            const by = it.UserID ?? it.UserId ?? it.userId ?? it.userID ?? '';
            if (tid) lockedMap.set(tid, by);
          });
        }
      }

      setLockedTaskIds(Array.from(lockedMap.keys()));

      const formatted = data.map((item, index) => {
        // NOTE: API provides Taskid (lowercase d per your code)
        const rawTaskId = item.Taskid ?? item.TaskId ?? item.TaskID ?? item.taskId ?? item.taskID;
        const taskIdStr = String(rawTaskId ?? '').trim();

        return {
          id: index + 1,
          memberId: item.MemberID,
          lastName: item.LastName,
          firstName: item.FirstName,
          dispensedDate: item.DispenseDate ? new Date(item.DispenseDate).toLocaleDateString() : '',
          medStartDate: item.MedStartDate ? new Date(item.MedStartDate).toLocaleDateString() : '',
          authNo: item.AuthNo || '',
          winAuthNo: item.WinAuthNo || '',
          dispenseBy: item.DispenseBy,
          priority: item.Priority || '',
          contract: item.Contract,
          taskId: rawTaskId,     // keep original field
          providerName: item.ProviderName,
          pharmacyId,
          userId,
          // 🔒 lock flags
          isLocked: lockedMap.has(taskIdStr),
          lockedBy: lockedMap.get(taskIdStr) || '',
        };
      });

      setRows(formatted);
    } catch (e) {
      console.error('[CIGNA] bindRows lock error:', e);
      // Fallback without lock flags
      const formatted = data.map((item, index) => ({
        id: index + 1,
        memberId: item.MemberID,
        lastName: item.LastName,
        firstName: item.FirstName,
        dispensedDate: item.DispenseDate ? new Date(item.DispenseDate).toLocaleDateString() : '',
        medStartDate: item.MedStartDate ? new Date(item.MedStartDate).toLocaleDateString() : '',
        authNo: item.AuthNo || '',
        winAuthNo: item.WinAuthNo || '',
        dispenseBy: item.DispenseBy,
        priority: item.Priority || '',
        contract: item.Contract,
        taskId: item.Taskid,
        providerName: item.ProviderName,
        pharmacyId,
        userId,
        isLocked: false,
        lockedBy: '',
      }));
      setRows(formatted);
    }
  };

  // Search with lock-aware binding
  const handleSearch = async () => {
    if (!selectedName || !searchText) {
      toast.error("Please select a value and enter text to search.");
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const pharmacyId = user?.pharmacyId;
    const userId = user?.userId;

    if (!pharmacyId || !userId) {
      toast.error("Missing pharmacy or user info. Please login again.");
      navigate('/error-page');
      return;
    }

    setSearchLoading(true);
    try {
      const orderResponse = await loadDispensedDataCigna(pharmacyId, selectedName, searchText, 1);
      console.log("[CIGNA SEARCH] params:", { PharmID: pharmacyId, Column: selectedName, Value: searchText, UserID: userId });

      if (orderResponse?.success && Array.isArray(orderResponse?.filterData)) {
        await bindRows(orderResponse.filterData, pharmacyId, userId);
      } else {
        setRows([]);
        toast.error("No data found for the search.");
      }
    } catch (error) {
      console.error('[CIGNA SEARCH] error:', error);
      toast.error("Error searching cost share details.");
      navigate('/error-page');
    } finally {
      setSearchLoading(false);
    }
  };

  // Initial load with lock-aware binding
  useEffect(() => {
    const fetchData = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      const pharmacyId = user?.pharmacyId;
      const userId = user?.userId;
      if (!pharmacyId) { setLoading(false); return; }

      try {
        const orderResponse = await loadDispensedDataCigna(pharmacyId, "", "", 1);
        if (orderResponse?.success && Array.isArray(orderResponse?.data)) {
          await bindRows(orderResponse.data, pharmacyId, userId);
        } else {
          setRows([]);
        }
      } catch (e) {
        console.error('[CIGNA INIT] error:', e);
        navigate('/error-page');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Row click: block if locked; otherwise continue to details
  const handleRowClick = async (params) => {
  const row = params.row;

  const contract   = row.contract;
  const taskId     = row.taskId;     // from item.Taskid
  const pharmacyId = row.pharmacyId;
  const userId     = row.userId;

  if (!contract || !taskId || !pharmacyId || !userId) {
    toast.error("Missing required task or user data.");
    navigate('/error-page');
    return;
  }

  // If someone else already locked it, block immediately
  if (row.isLocked) {
    const by = row.lockedBy ? ` by ${row.lockedBy}` : '';
    toast.error(`This task is currently locked${by}.`);
    return;
  }

  try {
    // 🔐 LOCK FIRST (same signature used elsewhere)
    const lockResp = await lockRXDetails(contract, userId, taskId, pharmacyId, "LOCK");
    if (!lockResp?.success) {
      toast.error(lockResp?.message || "This task is currently being used by another user.");
      return;
    }

    // (Optional) Optimistically reflect the lock locally
    setRows(prev =>
      prev.map(r => r.taskId === taskId ? { ...r, isLocked: true, lockedBy: userId } : r)
    );

    // Then fetch details and navigate
    const rxResponse = await loadRxDetails(taskId, contract, pharmacyId, userId);
    if (rxResponse?.data?.TaskDetail) {
      navigate('/dispensed-cigna-open-order', {
        state: { taskId, contract, pharmacyId, userId, rxData: rxResponse }
      });
    } else {
      toast.error("No prescription data found.");
    }
  } catch (err) {
    console.error("Failed to lock/load prescription details:", err);
    toast.error("Error loading prescription details.");
    navigate('/error-page');
  }
};


  return (
    <div style={{ position: 'relative' }}>
      <Toaster position="top-right" />
      <MenuBar />
      <div className='top-page-option'>
        <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center'}}>
          <a 
            href="#/home" 
            style={{ textDecoration: 'none', color: '#ec088c', display: 'inline-flex', alignItems: 'center' }}
          >
            <ChevronLeft color='#ec088c' size={18} />
            <span style={{ marginLeft: '5px' }}>Back</span>
          </a>
        </div>

        <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span> Dispensed Orders</span>
          <ChevronRight color='#ec088c' size={18} style={{ position: 'relative', top: '2px' }} />
          <span>Dispensed Orders-Cigna</span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', marginRight: '6.2%' }}>
            <FormControl size="small" sx={{ minWidth: 100, height: '30px', background: '#fff', borderRadius: '4px', marginRight: '10px' }}>
              <InputLabel sx={{ fontFamily: 'Calibri', marginBottom: '10px', background: '#fff', color: 'black', fontSize: '12px' }}>
                --Select--
              </InputLabel>
              <Select
                value={selectedName}
                onChange={(e) => setSelectedName(e.target.value)}
                label="Select"
                sx={{
                  fontFamily: 'Calibri',
                  fontSize: '12px',
                  border: '1px solid rgb(236, 151, 202)',
                  borderRadius: '15px',
                  height: '30px',
                  boxShadow: 'rgb(236, 151, 202)',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ec008c',
                    boxShadow: '0 0 6px rgba(236, 0, 140, 0.4)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ec008c',
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  --Select--
                </MenuItem>
                {names.map((item, idx) => (
                  <MenuItem key={idx} value={item.field} sx={{ fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              style={{
                padding: '5px 5px',
                border: '1px solid rgb(236, 151, 202)',
                borderRadius: '15px', outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.border = '1px solid #ec008c';
                e.target.style.boxShadow = '0 0 6px rgba(236, 0, 140, 0.4)';
              }}
              onBlur={(e) => {
                e.target.style.border = '1px solid rgb(236, 151, 202)';
                e.target.style.boxShadow = '0 1px 2px rgb(236, 151, 202)';
              }}
            />
            <button
              onClick={handleSearch}
              disabled={searchLoading}
              style={{ padding: '6px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {searchLoading ? <>Searching&nbsp;&nbsp;<CircularProgress size={15} sx={{ color: '#fff' }} /></> : 'Search'}
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '5px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer'}}
            >
              View All
            </button>
          </div>
        </div>
      </div>

      <div style={{ height: '60vh', width: '85%', margin: 'auto' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          onRowClick={handleRowClick}
          getRowClassName={(params) => (params.row.isLocked ? 'locked-task-row' : '')}
          sx={{
            fontSize: '12px',
            fontFamily: 'Segoe UI',
            '& .pink-header': {
              color: '#fff',
              backgroundColor: '#ec008c',
              fontWeight: 'bold',
              whiteSpace: 'normal !important',
              wordBreak: 'breakWord !important',
              overflowWrap: 'breakWord !important',
              lineHeight: '1.3 !important',
              textAlign: 'center',
              padding: '8px 2px !important',
            },
            '& .locked-task-row': {
              backgroundColor: 'cyan !important',
              color: 'black'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5 !important',
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
              boxShadow: 'none !important',
              backgroundColor: 'none !important',  
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '85%', margin: '10px auto 0 auto' }}>
        <Button
          variant="outlined"
          onClick={() => {
            const exportColumns = columns.map(({ field, headerName }) => ({ field, headerName }));
            exportToExcel(exportColumns, rows, 'Dispensed Orders-Cigna');
          }}
          sx={{ mb: 1, minWidth: 0, p: 0.5 }}
        >
          <FaFileExcel style={{ width: 30, height: 35 , color: '#008000'}} title="Download XLSX" alt="Excel" />
        </Button>
      </div>

      {searchLoading && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ border: '6px solid #f3f3f3', borderTop: '6px solid #EC088C', borderRadius: '50%', width: 48, height: 48, animation: 'spin 1s linear infinite' }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Searching...</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
      {loading && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ border: '6px solid #f3f3f3', borderTop: '6px solid #EC088C', borderRadius: '50%', width: 48, height: 48, animation: 'spin 1s linear infinite' }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Your request is being processed</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
    </div>
  );
};

export default DispensedOrderCignaGrid;
